import { ChevronLeft } from 'lucide-react';
import type { CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FALLBACK_PRIMARY } from '../lib/brand';
import { useCustomerBrand } from '../lib/brandContext';
import { useCustomerAuth } from '../useCustomerAuth';

/**
 * Persistent header for the signed-in customer portal (`/account/*`, `/book`).
 *
 * Mirrors the tenant landing page's header — tenant name left, one small
 * action right — so the portal reads as the same site. Sticky, translucent,
 * and it carries the tenant's primary colour on its own subtree (the pages
 * below wrap themselves in `BrandedShell`; the header sits outside that).
 *
 *   - `/account`            tenant name · · · · · · · · · · · · "Sign out"
 *   - `/account/<anything>` ‹ Account   tenant name  (nothing on the right)
 *   - `/book` signed in     ‹ Account   tenant name
 *   - `/book` guest         tenant name → `/`  · · · · · · · · · "Sign in"
 *
 * Every control is ≥ 44px tall (`min-h-11`) for touch.
 */
export function CustomerPortalHeader() {
  const { pathname } = useLocation();
  const { brand, loaded } = useCustomerBrand();
  const { user, signOut } = useCustomerAuth();

  const isAccountRoot = pathname === '/account' || pathname === '/account/';
  const isBook = pathname === '/book' || pathname.startsWith('/book/');
  const signedIn = user !== null;

  const showBack = !isAccountRoot && (!isBook || signedIn);
  const showSignOut = isAccountRoot && signedIn;
  const showSignIn = isBook && !signedIn;

  const primary = brand?.brand.primaryColour ?? FALLBACK_PRIMARY;
  const style: CSSProperties = {
    ['--brand-primary' as string]: primary,
    ['--primary' as string]: primary,
  };
  // Until the branding fetch settles, hold the space with a non-breaking
  // space so the header height does not jump when the name arrives.
  const tenantName = brand?.accountName ?? (loaded ? '' : ' ');
  const homeHref = signedIn ? '/account' : '/';

  return (
    <header
      data-testid="customer-portal-header"
      style={style}
      className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur"
    >
      <div className="mx-auto flex min-h-14 max-w-2xl items-center gap-2 px-4 sm:px-6">
        {showBack ? (
          <Link
            to="/account"
            aria-label="Back to account"
            className="-ml-2 inline-flex min-h-11 items-center gap-0.5 rounded-md pr-2 pl-1 text-sm text-primary hover:bg-muted"
          >
            <ChevronLeft size={18} aria-hidden="true" />
            Account
          </Link>
        ) : null}

        <Link
          to={homeHref}
          className="inline-flex min-h-11 min-w-0 flex-1 items-center truncate font-semibold text-foreground tracking-[-0.01em] no-underline"
          style={{ justifyContent: showBack ? 'flex-end' : 'flex-start' }}
        >
          <span className="truncate">{tenantName}</span>
        </Link>

        {showSignOut ? (
          <button
            type="button"
            onClick={() => void signOut()}
            className="-mr-2 inline-flex min-h-11 items-center rounded-md px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Sign out
          </button>
        ) : null}
        {showSignIn ? (
          <Link
            to="/sign-in?next=/book"
            className="-mr-2 inline-flex min-h-11 items-center rounded-md px-2 text-sm text-primary hover:bg-muted"
          >
            Sign in
          </Link>
        ) : null}
      </div>
    </header>
  );
}
